import {
  PolarstepsLocationPoint,
  PolarstepsStep,
  PolarstepsTrip,
  ParseResult,
} from '../types/polarsteps';

export class PolarstepsParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PolarstepsParseError';
  }
}

/**
 * Validates whether a raw coordinate pair is within geographical limits.
 */
export function isValidCoordinate(lat: unknown, lon: unknown): lat is number {
  return (
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    typeof lon === 'number' &&
    Number.isFinite(lon) &&
    lon >= -180 &&
    lon <= 180
  );
}

/**
 * Parses and sanitizes a locations.json file or string.
 * Supports both { "locations": [...] } and raw [...] structures.
 * Performs chronological sorting and deduplication.
 * Throws PolarstepsParseError on corrupted JSON or invalid structure.
 */
export function parseLocationsJson(rawInput: string | unknown): PolarstepsLocationPoint[] {
  let json: unknown;
  if (typeof rawInput === 'string') {
    try {
      json = JSON.parse(rawInput);
    } catch (e) {
      throw new PolarstepsParseError(`JSON parse error: ${(e as Error).message}`);
    }
  } else {
    json = rawInput;
  }

  // Handle schema polymorphism
  // Handle schema polymorphism
  let rawList: unknown[] = [];
  if (Array.isArray(json)) {
    rawList = json;
  } else if (typeof json === 'object' && json !== null) {
    const container = json as Record<string, unknown>;
    if (Array.isArray(container.locations)) {
      rawList = container.locations;
    } else if (Array.isArray(container.all_steps)) {
      // Coarse points from steps if locations array is not directly present
      rawList = (container.all_steps as Record<string, unknown>[])
        .filter((s) => s && typeof s.location === 'object' && s.location !== null)
        .map((s) => ({
          lat: (s.location as any).lat ?? (s.location as any).latitude,
          lon: (s.location as any).lon ?? (s.location as any).longitude,
          time: s.start_time,
        }));
    } else if (Array.isArray(container.trips) && container.trips.length > 0) {
      const firstTrip = container.trips[0] as Record<string, unknown>;
      if (Array.isArray(firstTrip.locations)) {
        rawList = firstTrip.locations;
      }
    } else {
      throw new PolarstepsParseError(
        'Unrecognized locations format: expected array or object containing "locations" or "all_steps".'
      );
    }
  } else {
    throw new PolarstepsParseError(
      'Unrecognized locations format: input must be a valid array or object.'
    );
  }

  const validPoints: PolarstepsLocationPoint[] = [];

  for (let i = 0; i < rawList.length; i++) {
    const item = rawList[i];
    if (typeof item !== 'object' || item === null) {
      continue;
    }

    const rec = item as Record<string, unknown>;
    const lat = typeof rec.lat === 'number' ? rec.lat : (typeof rec.latitude === 'number' ? rec.latitude : undefined);
    const lon = typeof rec.lon === 'number' ? rec.lon : (typeof rec.longitude === 'number' ? rec.longitude : undefined);
    let time = typeof rec.time === 'number' ? rec.time : (typeof rec.timestamp === 'number' ? rec.timestamp : undefined);
    const accuracy = typeof rec.accuracy === 'number' ? rec.accuracy : undefined;

    if (!isValidCoordinate(lat, lon)) {
      continue;
    }

    if (typeof time !== 'number' || !Number.isFinite(time) || time <= 0) {
      continue;
    }

    // Convert milliseconds to seconds if needed
    if (time > 1e11) {
      time = Math.round(time / 1000);
    }

    validPoints.push({ lat, lon: lon as number, time, accuracy });
  }

  if (validPoints.length === 0) {
    return [];
  }

  // 1. Critical sort: Mobile offline buffering causes out-of-order arrivals
  validPoints.sort((a, b) => a.time - b.time);

  // 2. Deduplicate identical timestamps (keep point with better accuracy)
  const deduped: PolarstepsLocationPoint[] = [validPoints[0]];
  for (let i = 1; i < validPoints.length; i++) {
    const prev = deduped[deduped.length - 1];
    const curr = validPoints[i];
    if (Math.abs(curr.time - prev.time) < 1e-4) {
      // If timestamps coincide, preserve the one with higher accuracy (smaller meter value)
      if (curr.accuracy !== undefined && (prev.accuracy === undefined || curr.accuracy < prev.accuracy)) {
        deduped[deduped.length - 1] = curr;
      }
    } else {
      deduped.push(curr);
    }
  }

  return deduped;
}

/**
 * Safe version of parseLocationsJson that returns a ParseResult instead of throwing.
 */
export function safeParseLocationsJson(rawInput: string | unknown): ParseResult<PolarstepsLocationPoint[]> {
  try {
    const data = parseLocationsJson(rawInput);
    return {
      success: true,
      data,
      errors: [],
      warnings: data.length === 0 ? ['locations.json contains zero valid GPS points.'] : [],
    };
  } catch (err: unknown) {
    return {
      success: false,
      errors: [err instanceof Error ? err.message : 'Unknown parsing error'],
      warnings: [],
    };
  }
}

/**
 * Parses and validates trip.json.
 * Throws PolarstepsParseError on malformed JSON or missing required fields.
 */
export function parseTripJson(rawInput: string | unknown): PolarstepsTrip {
  let json: unknown;
  if (typeof rawInput === 'string') {
    try {
      json = JSON.parse(rawInput);
    } catch (e) {
      throw new PolarstepsParseError(`JSON parse error: ${(e as Error).message}`);
    }
  } else {
    json = rawInput;
  }

  if (typeof json !== 'object' || json === null) {
    throw new PolarstepsParseError('trip.json root must be a non-null object.');
  }

  let raw = json as Record<string, unknown>;

  // Unwrap if root contains a trips array (common in Polarsteps full export)
  if (Array.isArray(raw.trips) && raw.trips.length > 0 && typeof raw.trips[0] === 'object') {
    raw = raw.trips[0] as Record<string, unknown>;
  }

  // Check required trip fields
  if (raw.id === undefined || raw.name === undefined || raw.start_date === undefined) {
    throw new PolarstepsParseError('Missing required trip attributes (id, name, start_date).');
  }

  const id = raw.id as number | string;
  const name = typeof raw.name === 'string' && raw.name.trim().length > 0 ? raw.name : 'Viaggio Polarsteps';
  const summary = typeof raw.summary === 'string' ? raw.summary : '';
  let startDate = typeof raw.start_date === 'number' ? raw.start_date : 0;
  if (startDate > 1e11) startDate = Math.round(startDate / 1000);
  let endDate = typeof raw.end_date === 'number' ? raw.end_date : startDate;
  if (endDate > 1e11) endDate = Math.round(endDate / 1000);
  const timezoneId =
    typeof raw.timezone_id === 'string' && raw.timezone_id.length > 0 ? raw.timezone_id : 'UTC';
  const totalKm = typeof raw.total_km === 'number' ? raw.total_km : undefined;

  let embeddedLocations: PolarstepsLocationPoint[] = [];
  if (Array.isArray(raw.locations)) {
    try {
      embeddedLocations = parseLocationsJson(raw.locations);
    } catch {}
  }

  const stepsList: PolarstepsStep[] = [];
  if (Array.isArray(raw.all_steps)) {
    for (const stepItem of raw.all_steps) {
      if (typeof stepItem !== 'object' || stepItem === null) continue;
      const s = stepItem as Record<string, unknown>;

      const stepId = (s.id as number | string) ?? Math.random().toString(36).substring(2);
      const locRaw =
        typeof s.location === 'object' && s.location !== null
          ? (s.location as Record<string, unknown>)
          : {};
      const locName =
        typeof locRaw.name === 'string' && locRaw.name.trim().length > 0
          ? locRaw.name.trim()
          : undefined;

      // Fallback name: name -> display_name -> location.name -> 'Tappa senza nome'
      const stepName =
        typeof s.name === 'string' && s.name.trim().length > 0
          ? s.name
          : typeof s.display_name === 'string' && s.display_name.trim().length > 0
          ? s.display_name
          : locName ?? 'Tappa senza nome';

      const description = typeof s.description === 'string' ? s.description : null;
      let startTime = typeof s.start_time === 'number' ? s.start_time : 0;
      if (startTime > 1e11) startTime = Math.round(startTime / 1000);
      let endTime = typeof s.end_time === 'number' ? s.end_time : null;
      if (endTime && endTime > 1e11) endTime = Math.round(endTime / 1000);
      const stepTz = typeof s.timezone_id === 'string' ? s.timezone_id : null;

      const lat = typeof locRaw.lat === 'number' ? locRaw.lat : (typeof locRaw.latitude === 'number' ? locRaw.latitude : undefined);
      const lon = typeof locRaw.lon === 'number' ? locRaw.lon : (typeof locRaw.longitude === 'number' ? locRaw.longitude : undefined);

      if (!isValidCoordinate(lat, lon)) {
        continue;
      }

      stepsList.push({
        id: stepId,
        name: stepName,
        description,
        start_time: startTime,
        end_time: endTime,
        timezone_id: stepTz,
        location: {
          name: typeof locRaw.name === 'string' ? locRaw.name : stepName,
          detail: typeof locRaw.detail === 'string' ? locRaw.detail : undefined,
          country_code: typeof locRaw.country_code === 'string' ? locRaw.country_code : undefined,
          lat,
          lon: lon as number,
        },
      });
    }
  }

  // Sort steps by start_time ascending
  stepsList.sort((a, b) => a.start_time - b.start_time);

  return {
    id,
    name,
    summary,
    start_date: startDate,
    end_date: endDate,
    timezone_id: timezoneId,
    total_km: totalKm,
    all_steps: stepsList,
    locations: embeddedLocations,
  };
}

/**
 * Safe version of parseTripJson that returns a ParseResult instead of throwing.
 */
export function safeParseTripJson(rawInput: string | unknown): ParseResult<PolarstepsTrip> {
  try {
    const data = parseTripJson(rawInput);
    return {
      success: true,
      data,
      errors: [],
      warnings: [],
    };
  } catch (err: unknown) {
    return {
      success: false,
      errors: [err instanceof Error ? err.message : 'Unknown parsing error'],
      warnings: [],
    };
  }
}

/**
 * Unifies parsed trip metadata with locations GPS points.
 */
export function unifyPolarstepsData(
  trip: PolarstepsTrip,
  locations: PolarstepsLocationPoint[]
): PolarstepsTrip {
  return {
    ...trip,
    locations,
  };
}

/**
 * Fuses trip.json and locations.json results into a single unified PolarstepsTrip entity.
 * Handles scenarios where only one of the two files is available.
 */
export function fusePolarstepsData(
  locationsResult?: ParseResult<PolarstepsLocationPoint[]>,
  tripResult?: ParseResult<PolarstepsTrip>
): ParseResult<PolarstepsTrip> {
  const errors: string[] = [];
  const warnings: string[] = [];

  const locations = locationsResult?.data ?? [];
  let trip = tripResult?.data;

  if (locationsResult?.errors) errors.push(...locationsResult.errors);
  if (tripResult?.errors) errors.push(...tripResult.errors);
  if (locationsResult?.warnings) warnings.push(...locationsResult.warnings);
  if (tripResult?.warnings) warnings.push(...tripResult.warnings);

  if (!trip) {
    if (locations.length === 0) {
      return { success: false, errors: ['Neither valid locations nor trip data provided.'], warnings };
    }
    // Synthesize default trip from locations
    const minTime = locations[0].time;
    const maxTime = locations[locations.length - 1].time;
    trip = {
      id: `synthetic-${minTime}`,
      name: 'La nostra Islanda',
      start_date: minTime,
      end_date: maxTime,
      timezone_id: 'UTC',
      all_steps: [],
      locations,
    };
    warnings.push('Created synthetic trip from GPS locations (no trip.json supplied).');
    return { success: true, data: trip, errors, warnings };
  }

  // Trip provided: attach locations if new locations were parsed, or retain existing embedded locations
  if (locations.length > 0) {
    trip.locations = locations;
  } else if (!trip.locations || trip.locations.length === 0) {
    if (trip.all_steps.length > 0) {
      warnings.push('No locations.json supplied: synthesizing coarse track from Step check-ins.');
      trip.locations = trip.all_steps.map((step) => ({
        lat: step.location.lat,
        lon: step.location.lon,
        time: step.start_time,
      }));
    } else {
      trip.locations = [];
    }
  }

  return { success: errors.length === 0, data: trip, errors, warnings };
}
